import React, { useEffect, useMemo, useRef, useState } from "react";
import { ContentContainer } from "../../../components/layout";
import {
  Button,
  LoadingComponent,
  NoDataFoundTemplate,
  Select,
} from "../../../components/core";
import { useLazyQuery, useQuery } from "@apollo/client";
import { GET_COLLECTION_SUMMARY } from "../../../constants/graphql/collection-summary-queries";
import { toUTCDateTimeString } from "../../../algorithms/date";
import PrimereactDateRange from "../../../components/core/date-range-primereact";
import { Card } from "primereact/card";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import GlobalButton from "../../../components/core/global-button";
import { formatCurrencyValue } from "../../../utils/formatters";
import GlobalSelect from "../../../components/core/global-select";
import GlobalMultiSelect from "../../../components/core/global-multiselect";
import GlobalPrimereactDateRange from "../../../components/core/global-primereact-daterange";
import GlobalDateRangeWithTime from "../../../components/core/global-primereact-daterange";
import { QUERY_LOCKER_TYPE } from "../../../constants/graphql/drop-off-graphql";
import { NEWEST_LOCKER_NAME_QUERY } from "../../../features/stay-time-in-locker/stay-time-query/stay-time-graphql";
import { getMaxDate } from "../../dashboard/view-package-history";

// Service type colours
const SERVICE_COLORS = {
  POD: "#F4D03F",
  Storage: "#2ECC71",
  Overstay: "#E74C3C",
};
// Currency colours — extendable as new currencies appear
const CURRENCY_COLOR_PALETTE = [
  "#3B82F6",
  "#F59E0B",
  "#10B981",
  "#EF4444",
  "#8B5CF6",
  "#06B6D4",
  "#F97316",
  "#EC4899",
];

const DATE_RANGE_PRESETS = [
  { name: "Last 7 days", value: "7" },
  { name: "Last 30 days", value: "30" },
  { name: "Last 90 days", value: "90" },
  { name: "Custom Range", value: "custom" },
];

const getDatesFromPreset = (preset) => {
  const end = new Date();
  const start = new Date();
  start.setDate(start.getDate() - parseInt(preset, 10));
  return { startDate: start, endDate: end };
};

const CollectionSummaryPage = () => {
  const operatorId = JSON.parse(localStorage.getItem("user")).operator_id;
  const maxDate = useMemo(() => getMaxDate(), []);

  const [datePreset, setDatePreset] = useState("30");
  const [isClicked, setIsClicked] = useState(false);
  const [filterVariables, setFilterVariables] = useState({
    // ...getDatesFromPreset("30"),
    startDate: maxDate,
    endDate: new Date(),
    lockerTypeId: -1,
    lockerIds: [],
    startTime: "00:00",
    endTime: "23:59",
  });
  const [chartData, setChartData] = useState([]); // per-locker stacked bar
  const [currencyChart, setCurrencyChart] = useState([]); // per-currency grouped bar
  const [currencies, setCurrencies] = useState([]); // unique currency list
  const [totals, setTotals] = useState(null);
  const [currencyTotals, setCurrencyTotals] = useState({}); // { PHP: { pod, storage, overstay, total }, MBOX: ... }
  const [doorSizeChart, setDoorSizeChart] = useState([]); // per-door-size grouped bar
  const [doorSizeTotals, setDoorSizeTotals] = useState({}); // { Small: { pod, storage, overstay, total, txPOD, ... } }
  const [rawData, setRawData] = useState([]);
  const [lockerType, setLockerType] = useState([]);
  const [lockerNameOptions, setLockerNameOptions] = useState([]);
  const [allLockersSelected, setAllLockersSelected] = useState(false);

  // chart capture refs (for PDF export)
  const doorSizeChartRef = useRef(null);
  const byLockerChartRef = useRef(null);
  const paymentMethodChartRef = useRef(null);

  // gql
  const fetchLockerType = useQuery(QUERY_LOCKER_TYPE);
  const [
    fetchLockerName,
    { data: lockerNameData, loading: lockerNameLoading },
  ] = useLazyQuery(NEWEST_LOCKER_NAME_QUERY, {
    variables: {
      operatorId: operatorId,
    },
  });

  const [fetchSummary, { loading }] = useLazyQuery(GET_COLLECTION_SUMMARY, {
    fetchPolicy: "network-only",
    onCompleted: (data) => {
      const rows = data?.collectionSummary ?? [];
      setRawData(rows);
      processData(rows);
    },
  });

  // useeffects & usememos
  useEffect(() => {
    if (fetchLockerType.data) {
      let tempData = [];
      fetchLockerType.data.lockerTypes.forEach((element, index) => {
        tempData.push({
          value: element.lockerTypeId,
          label: element.lockerType,
        });
      });
      setLockerType(tempData);
    }
  }, [fetchLockerType.data]);

  useEffect(() => {
    if (filterVariables.lockerTypeId !== "-1") {
      fetchLockerName({
        variables: {
          operatorId: operatorId,
          lockerTypeId: filterVariables.lockerTypeId,
        },
      });
    }
  }, [filterVariables.lockerTypeId, operatorId]);

  useEffect(() => {
    if (lockerNameData) {
      const tempData = lockerNameData.lockerInfo.map((element) => ({
        label: element.lockerName,
        value: element.lockerId,
      }));

      if (!allLockersSelected) {
        tempData.unshift({
          value: 0,
          label: "All Lockers",
        });
      }

      setLockerNameOptions(tempData);
    }
  }, [lockerNameData, allLockersSelected]);

  useEffect(() => {
    const selected = filterVariables.lockerIds;
    const allLockers = lockerNameOptions;

    const hasZeroValue = selected.some((item) => item.value === 0);

    if (hasZeroValue) {
      setAllLockersSelected(true);
    } else if (selected.length === 0) {
      setAllLockersSelected(false);
    } else if (selected.length !== allLockers.length) {
      setAllLockersSelected(false);
    } else {
      setAllLockersSelected(true);
    }
  }, [filterVariables.lockerIds, lockerNameOptions]);

  useEffect(() => {
    if (allLockersSelected && lockerNameOptions.length > 0) {
      const selectedLockers = lockerNameOptions.filter(
        (locker) => locker.value !== 0,
      );

      setFilterVariables((prev) => ({
        ...prev,
        lockerIds: selectedLockers,
      }));
    }
  }, [allLockersSelected, lockerNameOptions]);

  const processData = (rows) => {
    const byLocker = {};
    const byCurrency = {};
    const byDoorSize = {};
    let grandPod = 0,
      grandStorage = 0,
      grandOverstay = 0;

    rows.forEach((r) => {
      // --- per-locker aggregation ---
      if (!byLocker[r.lockerId]) {
        byLocker[r.lockerId] = {
          name: r.lockerName,
          POD: 0,
          Storage: 0,
          Overstay: 0,
        };
      }
      byLocker[r.lockerId].POD += r.totalPodAmount ?? 0;
      byLocker[r.lockerId].Storage += r.totalStorageAmount ?? 0;
      byLocker[r.lockerId].Overstay += r.totalOverstayAmount ?? 0;

      // --- per-currency aggregation ---
      const cur =
        r.paymentCurrency === "PHP"
          ? "PHP"
          : r.paymentCurrency === "PHMC"
            ? "MBOX COINS"
            : (r.paymentCurrency ?? "Unknown");
      if (!byCurrency[cur]) {
        byCurrency[cur] = {
          currency: cur,
          POD: 0,
          Storage: 0,
          Overstay: 0,
          total: 0,
          txPOD: 0,
          txStorage: 0,
          txOverstay: 0,
          txTotal: 0,
        };
      }
      byCurrency[cur].POD += r.totalPodAmount ?? 0;
      byCurrency[cur].Storage += r.totalStorageAmount ?? 0;
      byCurrency[cur].Overstay += r.totalOverstayAmount ?? 0;
      byCurrency[cur].total += r.totalAmount ?? 0;
      byCurrency[cur].txPOD += r.totalPodTransactions ?? 0;
      byCurrency[cur].txStorage += r.totalStorageTransactions ?? 0;
      byCurrency[cur].txOverstay += r.totalOverstayTransactions ?? 0;
      byCurrency[cur].txTotal += r.totalTransactions ?? 0;

      // --- per-door-size aggregation ---
      const ds = r.doorSize ?? "Unknown";
      if (!byDoorSize[ds]) {
        byDoorSize[ds] = {
          doorSize: ds,
          POD: 0,
          Storage: 0,
          Overstay: 0,
          total: 0,
          txPOD: 0,
          txStorage: 0,
          txOverstay: 0,
          txTotal: 0,
          byCurrency: {},
        };
      }
      byDoorSize[ds].POD += r.totalPodAmount ?? 0;
      byDoorSize[ds].Storage += r.totalStorageAmount ?? 0;
      byDoorSize[ds].Overstay += r.totalOverstayAmount ?? 0;
      byDoorSize[ds].total += r.totalAmount ?? 0;
      byDoorSize[ds].txPOD += r.totalPodTransactions ?? 0;
      byDoorSize[ds].txStorage += r.totalStorageTransactions ?? 0;
      byDoorSize[ds].txOverstay += r.totalOverstayTransactions ?? 0;
      byDoorSize[ds].txTotal += r.totalTransactions ?? 0;
      if (!byDoorSize[ds].byCurrency[cur]) {
        byDoorSize[ds].byCurrency[cur] = {
          POD: 0,
          Storage: 0,
          Overstay: 0,
          total: 0,
          txPOD: 0,
          txStorage: 0,
          txOverstay: 0,
          txTotal: 0,
        };
      }
      byDoorSize[ds].byCurrency[cur].POD += r.totalPodAmount ?? 0;
      byDoorSize[ds].byCurrency[cur].Storage += r.totalStorageAmount ?? 0;
      byDoorSize[ds].byCurrency[cur].Overstay += r.totalOverstayAmount ?? 0;
      byDoorSize[ds].byCurrency[cur].total += r.totalAmount ?? 0;
      byDoorSize[ds].byCurrency[cur].txPOD += r.totalPodTransactions ?? 0;
      byDoorSize[ds].byCurrency[cur].txStorage +=
        r.totalStorageTransactions ?? 0;
      byDoorSize[ds].byCurrency[cur].txOverstay +=
        r.totalOverstayTransactions ?? 0;
      byDoorSize[ds].byCurrency[cur].txTotal += r.totalTransactions ?? 0;

      grandPod += r.totalPodAmount ?? 0;
      grandStorage += r.totalStorageAmount ?? 0;
      grandOverstay += r.totalOverstayAmount ?? 0;
    });

    const grandTotal = grandPod + grandStorage + grandOverstay;

    setChartData(Object.values(byLocker));
    setCurrencyChart(Object.values(byCurrency));
    setCurrencies(Object.keys(byCurrency));
    setCurrencyTotals(byCurrency);
    // door sizes ordered Small → Medium → Large → others
    const DS_ORDER = ["Small", "Medium", "Large"];
    const sortedDoorSizes = Object.values(byDoorSize).sort((a, b) => {
      const ai = DS_ORDER.indexOf(a.doorSize);
      const bi = DS_ORDER.indexOf(b.doorSize);
      if (ai === -1 && bi === -1) return a.doorSize.localeCompare(b.doorSize);
      if (ai === -1) return 1;
      if (bi === -1) return -1;
      return ai - bi;
    });
    setDoorSizeChart(sortedDoorSizes);
    setDoorSizeTotals(byDoorSize);
    setTotals({
      pod: grandPod,
      storage: grandStorage,
      overstay: grandOverstay,
      total: grandTotal,
      podPct: grandTotal > 0 ? ((grandPod / grandTotal) * 100).toFixed(1) : 0,
      storagePct:
        grandTotal > 0 ? ((grandStorage / grandTotal) * 100).toFixed(1) : 0,
      overstayPct:
        grandTotal > 0 ? ((grandOverstay / grandTotal) * 100).toFixed(1) : 0,
    });
  };

  const handleSearch = () => {
    setIsClicked(true);

    // Build locker IDs — null means all lockers
    const lockerIdList = allLockersSelected
      ? null
      : filterVariables.lockerIds.length > 0
        ? filterVariables.lockerIds
            .filter((l) => l.value !== 0)
            .map((l) => parseInt(l.value, 10))
        : null;

    fetchSummary({
      variables: {
        operatorId,
        transactionDateStart: toUTCDateTimeString(
          filterVariables.startDate,
          filterVariables.startTime,
        ),
        transactionDateEnd: toUTCDateTimeString(
          filterVariables.endDate,
          filterVariables.endTime,
        ),
        ...(lockerIdList ? { lockerIds: lockerIdList } : {}),
      },
    });
  };

  const downloadDoorSizeCSV = () => {
    const headers = [
      "Door Size",
      "Currency",
      "POD Amount",
      "Storage Amount",
      "Overstay Amount",
      "Total Amount",
      "POD Transactions",
      "Storage Transactions",
      "Overstay Transactions",
      "Total Transactions",
    ];
    const rows = [headers];
    doorSizeChart.forEach((ds) => {
      Object.entries(ds.byCurrency).forEach(([cur, cv]) => {
        rows.push([
          ds.doorSize,
          cur,
          cv.POD.toFixed(2),
          cv.Storage.toFixed(2),
          cv.Overstay.toFixed(2),
          cv.total.toFixed(2),
          cv.txPOD,
          cv.txStorage,
          cv.txOverstay,
          cv.txTotal,
        ]);
      });
    });
    const csv = rows.map((r) => r.map((v) => `"${v}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "door-size-summary.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  const downloadCollectionSummaryCSV = () => {
    const escape = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const row = (...cells) => cells.map(escape).join(",");
    const f2 = (v) => (v ?? 0).toFixed(2);

    const fmt = (date) =>
      date instanceof Date
        ? date.toLocaleDateString("en-PH")
        : String(date ?? "");

    const startLabel = fmt(filterVariables.startDate);
    const endLabel = fmt(filterVariables.endDate);
    const now = new Date().toLocaleString("en-PH");
    const curList = currencies; // e.g. ["PHP", "MBOX COINS"]

    const lines = [];

    // ── Report header ────────────────────────────────
    lines.push(row("Collection Summary Report"));
    lines.push(
      row(
        `Date Range: ${startLabel} ${filterVariables.startTime} – ${endLabel} ${filterVariables.endTime}`,
      ),
    );
    lines.push(row(`Generated: ${now}`));
    lines.push("");

    // ── Section 1: Grand Summary (currencies as column groups) ──
    lines.push(row("GRAND SUMMARY"));
    const summaryHeader = ["Service Type"];
    curList.forEach((c) =>
      summaryHeader.push(`${c} Amount`, `${c} Transactions`),
    );
    lines.push(row(...summaryHeader));

    [
      { label: "POD", amtKey: "POD", txKey: "txPOD" },
      { label: "Storage Rental", amtKey: "Storage", txKey: "txStorage" },
      { label: "Overstay Fees", amtKey: "Overstay", txKey: "txOverstay" },
    ].forEach(({ label, amtKey, txKey }) => {
      const cells = [label];
      curList.forEach((c) => {
        const ct = currencyTotals[c] ?? {};
        cells.push(f2(ct[amtKey] ?? 0), ct[txKey] ?? 0);
      });
      lines.push(row(...cells));
    });
    const totalCells = ["Total"];
    curList.forEach((c) => {
      const ct = currencyTotals[c] ?? {};
      totalCells.push(f2(ct.total ?? 0), ct.txTotal ?? 0);
    });
    lines.push(row(...totalCells));
    lines.push("");

    // ── Section 2: By Locker (currencies as column groups) ───────
    lines.push(row("COLLECTIONS BY LOCKER"));
    const lockerHeader = ["Locker Name"];
    curList.forEach((c) =>
      lockerHeader.push(
        `${c} POD`,
        `${c} Storage`,
        `${c} Overstay`,
        `${c} Total`,
      ),
    );
    lines.push(row(...lockerHeader));

    // Build per-locker per-currency map from rawData
    const byLockerCur = {};
    rawData.forEach((r) => {
      const cur =
        r.paymentCurrency === "PHP"
          ? "PHP"
          : r.paymentCurrency === "PHMC"
            ? "MBOX COINS"
            : (r.paymentCurrency ?? "Unknown");
      if (!byLockerCur[r.lockerId])
        byLockerCur[r.lockerId] = { name: r.lockerName };
      if (!byLockerCur[r.lockerId][cur])
        byLockerCur[r.lockerId][cur] = {
          POD: 0,
          Storage: 0,
          Overstay: 0,
          total: 0,
        };
      byLockerCur[r.lockerId][cur].POD += r.totalPodAmount ?? 0;
      byLockerCur[r.lockerId][cur].Storage += r.totalStorageAmount ?? 0;
      byLockerCur[r.lockerId][cur].Overstay += r.totalOverstayAmount ?? 0;
      byLockerCur[r.lockerId][cur].total += r.totalAmount ?? 0;
    });
    Object.values(byLockerCur).forEach((locker) => {
      const cells = [locker.name];
      curList.forEach((c) => {
        const lc = locker[c] ?? { POD: 0, Storage: 0, Overstay: 0, total: 0 };
        cells.push(f2(lc.POD), f2(lc.Storage), f2(lc.Overstay), f2(lc.total));
      });
      lines.push(row(...cells));
    });
    lines.push("");

    // ── Section 3: By Door Size (currencies as column groups) ────
    if (doorSizeChart.length > 0) {
      lines.push(row("COLLECTIONS BY DOOR SIZE"));
      const dsHeader = ["Door Size"];
      curList.forEach((c) =>
        dsHeader.push(
          `${c} POD`,
          `${c} Storage`,
          `${c} Overstay`,
          `${c} Total`,
          `${c} POD Tx`,
          `${c} Storage Tx`,
          `${c} Overstay Tx`,
          `${c} Total Tx`,
        ),
      );
      lines.push(row(...dsHeader));

      doorSizeChart.forEach((ds) => {
        const cells = [ds.doorSize];
        curList.forEach((c) => {
          const cv = ds.byCurrency[c] ?? {
            POD: 0,
            Storage: 0,
            Overstay: 0,
            total: 0,
            txPOD: 0,
            txStorage: 0,
            txOverstay: 0,
            txTotal: 0,
          };
          cells.push(
            f2(cv.POD),
            f2(cv.Storage),
            f2(cv.Overstay),
            f2(cv.total),
            cv.txPOD,
            cv.txStorage,
            cv.txOverstay,
            cv.txTotal,
          );
        });
        lines.push(row(...cells));
      });
    }

    const csv = lines.join("\n");
    const blob = new Blob(["\uFEFF" + csv], {
      type: "text/csv;charset=utf-8;",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    const safeName = `collection-summary-${startLabel.replace(/\//g, "-")}-to-${endLabel.replace(/\//g, "-")}.csv`;
    a.download = safeName;
    a.click();
    URL.revokeObjectURL(url);
  };

  const downloadCollectionSummaryPDF = async () => {
    const { default: jsPDF } = await import("jspdf");
    await import("jspdf-autotable");
    const { default: html2canvas } = await import("html2canvas");

    const doc = new jsPDF({ orientation: "landscape", unit: "mm", format: "a4" });
    const pageW = doc.internal.pageSize.getWidth();
    const pageH = doc.internal.pageSize.getHeight();
    const margin = 14;
    let y = margin;

    const fmt = (date) =>
      date instanceof Date ? date.toLocaleDateString("en-PH") : String(date ?? "");
    const startLabel = fmt(filterVariables.startDate);
    const endLabel = fmt(filterVariables.endDate);
    const curList = currencies;

    // --- Title ---
    doc.setFontSize(16);
    doc.setFont("helvetica", "bold");
    doc.text("Collection Summary Report", margin, y);
    y += 7;
    doc.setFontSize(9);
    doc.setFont("helvetica", "normal");
    doc.text(
      `Date Range: ${startLabel} ${filterVariables.startTime} \u2013 ${endLabel} ${filterVariables.endTime}`,
      margin,
      y,
    );
    y += 5;
    doc.text(`Generated: ${new Date().toLocaleString("en-PH")}`, margin, y);
    y += 10;

    const headStyle = { fillColor: [254, 205, 8], textColor: [0, 0, 0], fontStyle: "bold" };
    const tableDefaults = {
      margin: { left: margin, right: margin },
      styles: { fontSize: 9 },
      headStyles: headStyle,
    };

    const addSection = (title) => {
      if (y + 30 > pageH - margin) { doc.addPage(); y = margin; }
      doc.setFontSize(11);
      doc.setFont("helvetica", "bold");
      doc.text(title, margin, y);
      y += 4;
    };

    // --- Grand Summary ---
    addSection("Grand Summary");
    const summaryHead = [["Service Type", ...curList.flatMap((c) => [`${c} Amount`, `${c} Tx`])]];
    const summaryBody = [
      ["POD",            ...curList.flatMap((c) => [(currencyTotals[c]?.POD      ?? 0).toFixed(2), currencyTotals[c]?.txPOD      ?? 0])],
      ["Storage Rental", ...curList.flatMap((c) => [(currencyTotals[c]?.Storage  ?? 0).toFixed(2), currencyTotals[c]?.txStorage  ?? 0])],
      ["Overstay Fees",  ...curList.flatMap((c) => [(currencyTotals[c]?.Overstay ?? 0).toFixed(2), currencyTotals[c]?.txOverstay ?? 0])],
      ["Total",          ...curList.flatMap((c) => [(currencyTotals[c]?.total    ?? 0).toFixed(2), currencyTotals[c]?.txTotal    ?? 0])],
    ];
    doc.autoTable({ ...tableDefaults, startY: y, head: summaryHead, body: summaryBody });
    y = doc.lastAutoTable.finalY + 8;

    // --- By Locker ---
    addSection("Collections by Locker");
    const lockerHead = [["Locker Name", ...curList.flatMap((c) => [`${c} POD`, `${c} Storage`, `${c} Overstay`, `${c} Total`])]];
    const byLockerCurPDF = {};
    rawData.forEach((r) => {
      const cur = r.paymentCurrency === "PHP" ? "PHP" : r.paymentCurrency === "PHMC" ? "MBOX COINS" : (r.paymentCurrency ?? "Unknown");
      if (!byLockerCurPDF[r.lockerId]) byLockerCurPDF[r.lockerId] = { name: r.lockerName };
      if (!byLockerCurPDF[r.lockerId][cur]) byLockerCurPDF[r.lockerId][cur] = { POD: 0, Storage: 0, Overstay: 0, total: 0 };
      byLockerCurPDF[r.lockerId][cur].POD      += r.totalPodAmount      ?? 0;
      byLockerCurPDF[r.lockerId][cur].Storage  += r.totalStorageAmount  ?? 0;
      byLockerCurPDF[r.lockerId][cur].Overstay += r.totalOverstayAmount ?? 0;
      byLockerCurPDF[r.lockerId][cur].total    += r.totalAmount         ?? 0;
    });
    const lockerBody = Object.values(byLockerCurPDF).map((locker) => [
      locker.name,
      ...curList.flatMap((c) => {
        const lc = locker[c] ?? { POD: 0, Storage: 0, Overstay: 0, total: 0 };
        return [lc.POD.toFixed(2), lc.Storage.toFixed(2), lc.Overstay.toFixed(2), lc.total.toFixed(2)];
      }),
    ]);
    doc.autoTable({ ...tableDefaults, startY: y, head: lockerHead, body: lockerBody });
    y = doc.lastAutoTable.finalY + 8;

    // --- By Door Size ---
    if (doorSizeChart.length > 0) {
      addSection("Collections by Door Size");
      const dsHead = [["Door Size", ...curList.flatMap((c) => [`${c} POD`, `${c} Storage`, `${c} Overstay`, `${c} Total`, `${c} POD Tx`, `${c} Storage Tx`, `${c} Overstay Tx`, `${c} Total Tx`])]];
      const dsBody = doorSizeChart.map((ds) => [
        ds.doorSize,
        ...curList.flatMap((c) => {
          const cv = ds.byCurrency[c] ?? { POD: 0, Storage: 0, Overstay: 0, total: 0, txPOD: 0, txStorage: 0, txOverstay: 0, txTotal: 0 };
          return [cv.POD.toFixed(2), cv.Storage.toFixed(2), cv.Overstay.toFixed(2), cv.total.toFixed(2), cv.txPOD, cv.txStorage, cv.txOverstay, cv.txTotal];
        }),
      ]);
      doc.autoTable({ ...tableDefaults, startY: y, head: dsHead, body: dsBody });
      y = doc.lastAutoTable.finalY + 8;
    }

    // --- Charts ---
    const addChart = async (ref, title) => {
      if (!ref.current) return;
      const canvas = await html2canvas(ref.current, { scale: 1.5, useCORS: true, backgroundColor: "#ffffff" });
      const imgData = canvas.toDataURL("image/png");
      const imgW = pageW - margin * 2;
      const imgH = (canvas.height / canvas.width) * imgW;
      if (y + imgH + 14 > pageH - margin) { doc.addPage(); y = margin; }
      doc.setFontSize(11);
      doc.setFont("helvetica", "bold");
      doc.text(title, margin, y);
      y += 4;
      doc.addImage(imgData, "PNG", margin, y, imgW, imgH);
      y += imgH + 10;
    };

    if (doorSizeChart.length > 0) await addChart(doorSizeChartRef, "Chart: Collections by Door Size");
    await addChart(byLockerChartRef, "Chart: Collections by Locker");
    if (currencyChart.length > 0) await addChart(paymentMethodChartRef, "Chart: Payment Method Comparison");

    const safeName = `collection-summary-${startLabel.replace(/\//g, "-")}-to-${endLabel.replace(/\//g, "-")}.pdf`;
    doc.save(safeName);
  };

  const handleFilterChange = (e, name) => {
    let value = e.target.value;

    setFilterVariables((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  return (
    <ContentContainer containerTitle="Collection Summary">
      {/* ── Filters ─────────────────────────────────── */}
      <div className="mb-4">
        <div className="grid grid-cols-12 items-center space-y-2">
          <div className="text-base col-span-10 font-semibold">Locker:</div>
          {/* locker type */}
          <div className="col-span-6 pl-4">
            <div className="grid grid-cols-10 items-center">
              <div className="text-base col-span-3">Locker Type:</div>
              <GlobalSelect
                items={[
                  { value: -1, label: "-- ALL Locker Types --" },
                  ...lockerType,
                ]}
                value={filterVariables.lockerTypeId}
                parentClassName={`col-span-7`}
                onChange={(e) => handleFilterChange(e, "lockerTypeId")}
              />
            </div>
          </div>

          <div className="col-span-6" />

          {/* locker name */}
          <div className="col-span-6 pl-4">
            <div className="grid grid-cols-10 items-center">
              <div className="text-base col-span-3">Locker Name:</div>
              <GlobalMultiSelect
                items={lockerNameOptions}
                placeholder={`${
                  filterVariables.lockerTypeId == -1
                    ? "ALL Lockers"
                    : "Select..."
                }`}
                disabled={filterVariables.lockerTypeId == -1}
                value={filterVariables.lockerIds}
                className={`col-span-7`}
                selectClassName={`w-full`}
                onChange={(e) => handleFilterChange(e, "lockerIds")}
              />
            </div>
          </div>

          <div className="col-span-6" />

          {/* date range */}
          <div className="col-span-8">
            <div className="grid grid-cols-10 items-center">
              <div className="text-base col-span-10 font-semibold">
                Date Range:
              </div>
              <PrimereactDateRange
                className={"pl-4 col-span-10"}
                startDate={filterVariables.startDate}
                endDate={filterVariables.endDate}
                startTime={filterVariables.startTime}
                endTime={filterVariables.endTime}
                max={new Date()} // Today's date as the maximum
                min={new Date("2021-01-01")} // Jan 1, 2021, as the minimum
                startDateName="startDate"
                endDateName="endDate"
                startTimeName="startTime"
                endTimeName="endTime"
                timeOnly={true}
                readOnlyInput={true}
                // yearRange="2001:2021"
                onChange={(event, fieldName) => {
                  handleFilterChange(event, fieldName);
                }}
              />
            </div>
          </div>
        </div>

        <div className="flex justify-start gap-2 mt-4">
          <GlobalButton
            isSecondary
            icon={`pi pi-undo`}
            buttonClassName={`font-normal`}
            onClick={() => {
              setFilterVariables({
                startDate: maxDate,
                endDate: new Date(),
                lockerTypeId: -1,
                lockerIds: [],
                startTime: "00:00",
                endTime: "23:59",
              });
              setAllLockersSelected(false);
              setIsClicked(false);
              setTotals(null);
              setRawData([]);
            }}
          >
            {`Reset`}
          </GlobalButton>
          <GlobalButton
            isPrimary
            icon={`pi pi-search`}
            onClick={handleSearch}
            // disabled={isSaving}
            // buttonClassName={`px-5 py-2 text-sm font-semibold rounded-lg transition-colors active:scale-95 `}
          >
            {`Search`}
          </GlobalButton>
        </div>
      </div>

      {loading && <LoadingComponent />}

      {isClicked && !loading && !totals && <NoDataFoundTemplate />}

      {isClicked &&
        !loading &&
        totals &&
        (() => {
          const grandTotal = totals.total;
          return (
            <>
              {/* ── Download buttons ─────────────────────────── */}
              <div className="flex justify-end gap-2 mb-3">
                <button
                  onClick={downloadCollectionSummaryCSV}
                  className="flex items-center gap-1.5 text-sm px-4 py-2 rounded-lg border border-gray-300 bg-white hover:bg-gray-50 text-gray-700 font-medium transition-colors shadow-sm"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M4 16v2a2 2 0 002 2h12a2 2 0 002-2v-2M7 10l5 5 5-5M12 15V3" />
                  </svg>
                  Download Report (CSV)
                </button>
                <button
                  onClick={downloadCollectionSummaryPDF}
                  className="flex items-center gap-1.5 text-sm px-4 py-2 rounded-lg border border-yellow-400 bg-yellow-50 hover:bg-yellow-100 text-yellow-800 font-medium transition-colors shadow-sm"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M4 16v2a2 2 0 002 2h12a2 2 0 002-2v-2M7 10l5 5 5-5M12 15V3" />
                  </svg>
                  Download PDF (with charts)
                </button>
              </div>

              {/* ── 1. Total Collections ─────────────────────── */}
              <div className="rounded-xl border bg-white shadow-sm p-5 mb-4">
                <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-4">
                  Total Collections
                </p>
                <div className="space-y-4">
                  {currencies.map((cur, i) => {
                    const c = currencyTotals[cur];
                    const pct =
                      grandTotal > 0 ? (c.total / grandTotal) * 100 : 0;
                    const accent =
                      CURRENCY_COLOR_PALETTE[i % CURRENCY_COLOR_PALETTE.length];
                    return (
                      <div key={cur}>
                        <div className="flex justify-between items-baseline mb-1.5">
                          <div className="flex items-center gap-2">
                            <span
                              className="inline-block w-2.5 h-2.5 rounded-full"
                              style={{ backgroundColor: accent }}
                            />
                            <span className="text-sm font-semibold text-gray-700">
                              {cur}
                            </span>
                          </div>
                          <div className="text-sm text-right">
                            <span className="font-bold">
                              {formatCurrencyValue(c.total, cur)}
                            </span>
                            <span className="text-gray-400 ml-2">
                              {c.txTotal.toLocaleString()} tx
                            </span>
                            <span className="text-gray-400 ml-2">
                              ({pct.toFixed(1)}%)
                            </span>
                          </div>
                        </div>
                        <div className="h-6 rounded-full bg-gray-100 overflow-hidden">
                          <div
                            className="h-full rounded-full flex items-center justify-end pr-2 text-white text-xs font-bold"
                            style={{
                              width: `${Math.max(pct, 0.5)}%`,
                              backgroundColor: accent,
                            }}
                          >
                            {pct > 8 ? `${pct.toFixed(1)}%` : ""}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* ── 2. By Service Type ───────────────────────── */}
              {currencies.length > 0 && (
                <div className="rounded-xl border bg-white shadow-sm p-5 mb-4">
                  <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-4">
                    Total Collections (By Service Type)
                  </p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {currencies.map((cur, i) => {
                      const c = currencyTotals[cur];
                      const accent =
                        CURRENCY_COLOR_PALETTE[
                          i % CURRENCY_COLOR_PALETTE.length
                        ];
                      const services = [
                        {
                          label: "POD",
                          amount: c.POD,
                          count: c.txPOD,
                          color: SERVICE_COLORS.POD,
                        },
                        {
                          label: "Storage Rental",
                          amount: c.Storage,
                          count: c.txStorage,
                          color: SERVICE_COLORS.Storage,
                        },
                        {
                          label: "Overstay Fees",
                          amount: c.Overstay,
                          count: c.txOverstay,
                          color: SERVICE_COLORS.Overstay,
                        },
                      ];
                      return (
                        <div
                          key={cur}
                          className="rounded-lg border p-4"
                          style={{
                            borderLeftColor: accent,
                            borderLeftWidth: 4,
                          }}
                        >
                          <div className="flex items-center justify-between mb-3">
                            <div className="flex items-center gap-2">
                              <span
                                className="inline-block w-2.5 h-2.5 rounded-full"
                                style={{ backgroundColor: accent }}
                              />
                              <span className="text-sm font-semibold">
                                {cur}
                              </span>
                            </div>
                            <span className="text-sm font-bold">
                              {formatCurrencyValue(c.total, cur)}
                            </span>
                          </div>
                          <div className="space-y-3">
                            {services.map(({ label, amount, count, color }) => {
                              const svcPct =
                                c.total > 0 ? (amount / c.total) * 100 : 0;
                              return (
                                <div key={label}>
                                  <div className="flex justify-between text-xs mb-1">
                                    <div className="flex items-center gap-1.5">
                                      <span
                                        className="inline-block w-2 h-2 rounded-full"
                                        style={{ backgroundColor: color }}
                                      />
                                      <span className="text-gray-600">
                                        {label}
                                      </span>
                                    </div>
                                    <span className="font-medium text-gray-700">
                                      {formatCurrencyValue(amount, cur)}{" "}
                                      <span className="text-gray-400">
                                        ({count} transaction(s))
                                      </span>
                                    </span>
                                  </div>
                                  <div className="h-1.5 rounded-full bg-gray-100 overflow-hidden">
                                    <div
                                      className="h-full rounded-full"
                                      style={{
                                        width: `${svcPct}%`,
                                        backgroundColor: color,
                                      }}
                                    />
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {chartData.length === 0 ? (
                <NoDataFoundTemplate />
              ) : (
                <>
                  {/* ── 3. By Door Size ──────────────────────────── */}
                  {doorSizeChart.length > 0 && (
                    <div ref={doorSizeChartRef}>
                    <Card className="mb-4">
                      <p className="font-bold text-center mb-2">
                        Total Collections (By Door Size)
                      </p>
                      <p className="text-xs text-center text-gray-400 mb-3">
                        POD, Storage &amp; Overstay amounts — grouped by door
                        size
                      </p>
                      <ResponsiveContainer width="100%" height={300}>
                        <BarChart
                          data={doorSizeChart}
                          margin={{ top: 10, right: 20, left: 20, bottom: 10 }}
                        >
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="doorSize" tick={{ fontSize: 12 }} />
                          <YAxis
                            tickFormatter={(v) => v.toLocaleString()}
                            label={{
                              value: "Amount",
                              angle: -90,
                              position: "insideLeft",
                              offset: -5,
                            }}
                          />
                          <Tooltip
                            formatter={(val, name) => [
                              formatCurrencyValue(val),
                              name,
                            ]}
                          />
                          <Legend verticalAlign="top" />
                          <Bar dataKey="POD" fill={SERVICE_COLORS.POD} />
                          <Bar
                            dataKey="Storage"
                            fill={SERVICE_COLORS.Storage}
                          />
                          <Bar
                            dataKey="Overstay"
                            fill={SERVICE_COLORS.Overstay}
                          />
                        </BarChart>
                      </ResponsiveContainer>
                    </Card>
                    </div>
                  )}

                  {/* ── 4. By Locker ─────────────────────────────── */}
                  <div ref={byLockerChartRef}>
                  <Card className="mb-4">
                    <p className="font-bold text-center mb-2">
                      Total Collections (By Locker)
                    </p>
                    <ResponsiveContainer width="100%" height={400}>
                      <BarChart
                        data={chartData}
                        margin={{ top: 10, right: 20, left: 20, bottom: 60 }}
                      >
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis
                          dataKey="name"
                          angle={-35}
                          textAnchor="end"
                          interval={0}
                          tick={{ fontSize: 11 }}
                        />
                        <YAxis
                          tickFormatter={(v) => v.toLocaleString()}
                          label={{
                            value: "Amount",
                            angle: -90,
                            position: "insideLeft",
                            offset: -5,
                          }}
                        />
                        <Tooltip
                          formatter={(val, name) => [
                            formatCurrencyValue(val),
                            name,
                          ]}
                        />
                        <Legend verticalAlign="top" />
                        <Bar
                          dataKey="POD"
                          stackId="a"
                          fill={SERVICE_COLORS.POD}
                        />
                        <Bar
                          dataKey="Storage"
                          stackId="a"
                          fill={SERVICE_COLORS.Storage}
                        />
                        <Bar
                          dataKey="Overstay"
                          stackId="a"
                          fill={SERVICE_COLORS.Overstay}
                        />
                      </BarChart>
                    </ResponsiveContainer>
                  </Card>
                  </div>

                  {/* ── 5. Payment Method Comparison ─────────────── */}
                  {currencyChart.length > 0 && (
                    <div ref={paymentMethodChartRef}>
                    <Card className="mb-4">
                      <p className="font-bold text-center mb-2">
                        Payment Method Comparison
                      </p>
                      <p className="text-xs text-center text-gray-400 mb-3">
                        POD, Storage &amp; Overstay amounts — grouped by payment
                        currency
                      </p>
                      <ResponsiveContainer width="100%" height={300}>
                        <BarChart
                          data={currencyChart}
                          margin={{ top: 10, right: 20, left: 20, bottom: 10 }}
                        >
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="currency" tick={{ fontSize: 12 }} />
                          <YAxis
                            tickFormatter={(v) => v.toLocaleString()}
                            label={{
                              value: "Amount",
                              angle: -90,
                              position: "insideLeft",
                              offset: -5,
                            }}
                          />
                          <Tooltip
                            formatter={(val, name) => [
                              formatCurrencyValue(val),
                              name,
                            ]}
                          />
                          <Legend verticalAlign="top" />
                          <Bar dataKey="POD" fill={SERVICE_COLORS.POD} />
                          <Bar
                            dataKey="Storage"
                            fill={SERVICE_COLORS.Storage}
                          />
                          <Bar
                            dataKey="Overstay"
                            fill={SERVICE_COLORS.Overstay}
                          />
                        </BarChart>
                      </ResponsiveContainer>
                    </Card>
                    </div>
                  )}

                  {/* ── 6. Door Size Summary ─────────────────────── */}
                  {doorSizeChart.length > 0 && (
                    <div className="rounded-xl border bg-white shadow-sm p-5">
                      <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
                        <p className="font-semibold text-gray-700">
                          Door Size Summary
                        </p>
                        <button
                          onClick={downloadDoorSizeCSV}
                          className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg border border-gray-300 bg-gray-50 hover:bg-gray-100 text-gray-600 transition-colors"
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            className="w-3.5 h-3.5"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                            strokeWidth={2}
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              d="M4 16v2a2 2 0 002 2h12a2 2 0 002-2v-2M7 10l5 5 5-5M12 15V3"
                            />
                          </svg>
                          Download CSV
                        </button>
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
                        {doorSizeChart.map((ds) => (
                          <div
                            key={ds.doorSize}
                            className="rounded-lg border p-4 bg-gray-50 min-w-0"
                          >
                            <div className="flex flex-wrap justify-between items-baseline gap-x-2 mb-2">
                              <span className="font-semibold text-sm">
                                {ds.doorSize}
                              </span>
                              <span className="text-xs text-gray-400 whitespace-nowrap">
                                {ds.txTotal.toLocaleString()} tx
                              </span>
                            </div>
                            {Object.entries(ds.byCurrency).map(
                              ([cur, cv], ci) => {
                                const accent =
                                  CURRENCY_COLOR_PALETTE[
                                    ci % CURRENCY_COLOR_PALETTE.length
                                  ];
                                const fmtCur =
                                  cur === "MBOX COINS" ? "MBOX" : "PHP";
                                return (
                                  <div
                                    key={cur}
                                    className="mt-2 pt-2 border-t first:border-t-0 first:pt-0 first:mt-0"
                                  >
                                    <div className="flex flex-wrap justify-between items-baseline gap-x-2 mb-1.5">
                                      <span
                                        className="text-xs font-semibold shrink-0"
                                        style={{ color: accent }}
                                      >
                                        {cur}
                                      </span>
                                      <span className="text-xs font-bold whitespace-nowrap">
                                        {formatCurrencyValue(cv.total, fmtCur)}
                                      </span>
                                    </div>
                                    <div className="text-xs text-gray-500 space-y-1">
                                      {[
                                        {
                                          label: "POD",
                                          amount: cv.POD,
                                          count: cv.txPOD,
                                          color: SERVICE_COLORS.POD,
                                        },
                                        {
                                          label: "Storage",
                                          amount: cv.Storage,
                                          count: cv.txStorage,
                                          color: SERVICE_COLORS.Storage,
                                        },
                                        {
                                          label: "Overstay",
                                          amount: cv.Overstay,
                                          count: cv.txOverstay,
                                          color: SERVICE_COLORS.Overstay,
                                        },
                                      ].map(
                                        ({ label, amount, count, color }) => (
                                          <div
                                            key={label}
                                            className="flex flex-wrap justify-between items-center gap-x-2"
                                          >
                                            <span
                                              className="shrink-0"
                                              style={{ color }}
                                            >
                                              ● {label}
                                            </span>
                                            <span className="whitespace-nowrap">
                                              {formatCurrencyValue(
                                                amount,
                                                fmtCur,
                                              )}{" "}
                                              <span className="text-gray-400">
                                                ({count})
                                              </span>
                                            </span>
                                          </div>
                                        ),
                                      )}
                                    </div>
                                  </div>
                                );
                              },
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </>
              )}
            </>
          );
        })()}
    </ContentContainer>
  );
};

export default CollectionSummaryPage;
